import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography
} from '@mui/material';

import { CloseOutlined } from '@mui/icons-material';

import OrderEntry from '../../../../../Common/components/Template/OrderEntry';
import PastDiag from '../../../../../Common/components/LabOrder/PastDiag';

import { OrdersDialogStyles } from '../../../../../Common/components/Template/OrdersDialogStyles';

import {
  dialogActionSx,
  dialogHeaderStyles
} from '../../../../../Common/components/Styles/InputStyles';

const defaultDiagnoses = [
  {
    id: 1,
    code: 'A37.90',
    description: 'Pertussis'
  },
  {
    id: 2,
    code: 'A49.02',
    description: 'MSSA Bacteremia'
  },
  {
    id: 3,
    code: 'A49.03',
    description: 'Unspecified Sexually Transmitted Disease'
  },
  {
    id: 4,
    code: 'D64.9',
    description: 'Anemia, Unspecified'
  },
  {
    id: 5,
    code: 'E039',
    description: 'Hypothyroidism'
  }
];

export const ORDER_TYPES = {
  LAB: 'lab',
  IMAGING: 'imaging',
  PHYSICIAN: 'physician'
};

const orderConfigurations = {
  [ORDER_TYPES.LAB]: {
    title: 'Laboratory Orders',
    subtitle: 'Review and manage laboratory orders for the encounter.',
    orderTitle: 'Laboratory Orders',
    placeholder: 'Select an order template, or input order details...',
    payloadKey: 'labOrder'
  },

  [ORDER_TYPES.IMAGING]: {
    title: 'Imaging Orders',
    subtitle: 'Review and manage diagnostic imaging orders for the encounter.',
    orderTitle: 'Imaging Orders',
    placeholder:
      'Select an imaging template, or input imaging order details...',
    payloadKey: 'imagingOrder'
  },

  [ORDER_TYPES.PHYSICIAN]: {
    title: 'Physician Orders',
    subtitle: 'Review and manage physician orders for the encounter.',
    orderTitle: 'Physician Orders',
    placeholder: 'Select a physician order template, or input order details...',
    payloadKey: 'physicianOrder'
  }
};

const OrdersDialog = ({
  open,
  type,
  initialValue,
  templateValue,
  diagnoses,
  onClose,
  onSignAndClose,
  onSignAndPrint,
  onOpenTemplates,
  onOpenDiagnosisTemplates
}) => {
  const config = orderConfigurations[type];

  const [orderValue, setOrderValue] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [selectedDiagnoses, setSelectedDiagnoses] = useState([]);
  
useEffect(() => {
  if (!open) return;

  setOrderValue(String(initialValue?.[config.payloadKey] || ''));
  setDiagnosis(String(initialValue?.diagnosis || ''));

  setSelectedDiagnoses(
    Array.isArray(initialValue?.diagnoses)
      ? initialValue.diagnoses
      : []
  );
}, [open, initialValue, config.payloadKey]);
  
useEffect(() => {
  if (!open || !templateValue) return;

  setOrderValue(currentValue => {
    const existingValue = currentValue.trim();
    const selectedTemplate = templateValue.trim();

    if (!selectedTemplate) {
      return currentValue;
    }

    if (!existingValue) {
      return selectedTemplate;
    }

    if (existingValue.includes(selectedTemplate)) {
      return currentValue;
    }

    return `${existingValue}\n${selectedTemplate}`;
  });
}, [open, templateValue]);
  const selectedDiagnosisIds = useMemo(
    () => selectedDiagnoses.map(item => item.id),
    [selectedDiagnoses]
  );

  const isValid = useMemo(
    () => Boolean(orderValue.trim() && diagnosis.trim()),
    [orderValue, diagnosis]
  );

  const handleSelectDiagnosis = selectedDiagnosis => {
    const diagnosisText = `${selectedDiagnosis.code}: ${selectedDiagnosis.description}`;

    setSelectedDiagnoses(currentDiagnoses => [
      ...currentDiagnoses,
      {
        ...selectedDiagnosis,
        selectionId: `${selectedDiagnosis.id}-${Date.now()}-${Math.random()}`
      }
    ]);

    setDiagnosis(currentValue => {
      const existingValue = currentValue.trim();

      return existingValue
        ? `${existingValue}\n${diagnosisText}`
        : diagnosisText;
    });
  };

  const getPayload = () => ({
    type,
    [config.payloadKey]: orderValue.trim(),
    diagnosis: diagnosis.trim(),
    diagnoses: selectedDiagnoses,
    diagnosisIds: selectedDiagnosisIds
  });

  const handleSignAndClose = () => {
    if (!isValid) return;

    onSignAndClose(getPayload());
  };

  const handleSignAndPrint = () => {
    if (!isValid) return;

    onSignAndPrint(getPayload());
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      scroll="paper"
      slotProps={{
        paper: {
          sx: OrdersDialogStyles.paper
        }
      }}>
      <DialogTitle sx={dialogHeaderStyles.dialogheader}>
        <Box>
          <Typography sx={dialogHeaderStyles.title}>{config.title}</Typography>

          <Typography sx={dialogHeaderStyles.subtitle}>
            {config.subtitle}
          </Typography>
        </Box>

        <IconButton
          size="small"
          aria-label={`Close ${config.title}`}
          onClick={onClose}
          sx={dialogHeaderStyles.dialogheaderclose}>
          <CloseOutlined sx={dialogHeaderStyles.closeIcon} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={OrdersDialogStyles.content}>
        <Grid container spacing={1.5} sx={OrdersDialogStyles.contentGrid}>
          <Grid size={{ xs: 12, md: 8 }}>
            <OrderEntry
              orderTitle={config.orderTitle}
              orderPlaceholder={config.placeholder}
              orderValue={orderValue}
              diagnosis={diagnosis}
              onOrderChange={setOrderValue}
              onDiagnosisChange={setDiagnosis}
              onOpenOrderTemplates={onOpenTemplates}
              onOpenDiagnosisTemplates={onOpenDiagnosisTemplates}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <PastDiag diagnoses={diagnoses} onSelect={handleSelectDiagnosis} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={dialogActionSx}>
        <Box sx={OrdersDialogStyles.footer}>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            sx={OrdersDialogStyles.cancelButton}>
            Cancel
          </Button>

          <Button
            variant="contained"
            size="small"
            disabled={!isValid}
            onClick={handleSignAndClose}
            sx={OrdersDialogStyles.signCloseButton}>
            E-Sign &amp; Close
          </Button>

          <Button
            variant="contained"
            size="small"
            disabled={!isValid}
            onClick={handleSignAndPrint}
            sx={OrdersDialogStyles.signPrintButton}>
            E-Sign &amp; Print
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
OrdersDialog.propTypes = {
  open: PropTypes.bool,
  templateValue: PropTypes.string,

  type: PropTypes.oneOf([
    ORDER_TYPES.LAB,
    ORDER_TYPES.IMAGING,
    ORDER_TYPES.PHYSICIAN
  ]),

  initialValue: PropTypes.shape({
    labOrder: PropTypes.string,
    imagingOrder: PropTypes.string,
    physicianOrder: PropTypes.string,
    diagnosis: PropTypes.string,
    diagnoses: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([
          PropTypes.number,
          PropTypes.string
        ]),
        code: PropTypes.string,
        description: PropTypes.string
      })
    )
  }),

  diagnoses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
      ]).isRequired,
      code: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired
    })
  ),

  onClose: PropTypes.func,
  onSignAndClose: PropTypes.func,
  onSignAndPrint: PropTypes.func,
  onOpenTemplates: PropTypes.func,
  onOpenDiagnosisTemplates: PropTypes.func
};

OrdersDialog.defaultProps = {
  open: false,
  type: ORDER_TYPES.LAB,
  initialValue: null,
  templateValue: '',
  diagnoses: defaultDiagnoses,
  onClose: () => {},
  onSignAndClose: () => {},
  onSignAndPrint: () => {},
  onOpenTemplates: () => {},
  onOpenDiagnosisTemplates: () => {}
};

export default OrdersDialog;
